import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractText } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useFolders } from '@/features/sources/hooks/useFolders';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  FileText,
  Calendar,
  Users,
  BookOpen,
  ExternalLink,
  Download,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { AlertResult } from '@/types/database';

export function Import() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const paperId = searchParams.get('paper_id');
  const alertId = searchParams.get('alert_id');

  const [alertResult, setAlertResult] = useState<AlertResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [createdSourceId, setCreatedSourceId] = useState<string | null>(null);
  const [folderId, setFolderId] = useState<string>('none');

  const { data: folders } = useFolders();

  // Load alert result data
  useEffect(() => {
    async function loadResult() {
      if (!paperId || !alertId) {
        setError('Missing paper_id or alert_id in URL.');
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('alert_results')
        .select('*')
        .eq('alert_id', alertId)
        .eq('paper_id', paperId)
        .single();

      if (fetchError || !data) {
        setError('Paper not found. It may have been deleted or the link is invalid.');
        setLoading(false);
        return;
      }

      const result = data as AlertResult;
      setAlertResult(result);

      if (result.imported) {
        setImported(true);
        setCreatedSourceId(result.source_id);
      }

      setLoading(false);
    }

    loadResult();
  }, [paperId, alertId]);

  async function handleImport() {
    if (!user || !alertResult) return;

    setImporting(true);
    try {
      // Create a source record from the alert result metadata
      const { data: source, error: insertError } = await supabase
        .from('sources')
        .insert({
          user_id: user.id,
          file_name: `${alertResult.title || 'Untitled'}.pdf`,
          file_type: 'application/pdf',
          title: alertResult.title,
          authors: alertResult.authors,
          publication_year: alertResult.year,
          journal: alertResult.journal,
          doi: alertResult.doi,
          abstract: alertResult.abstract,
          source_url: alertResult.open_access_pdf_url || alertResult.url,
          extraction_status: 'pending',
          metadata: {
            import_source: 'paper_alert',
            semantic_scholar_id: alertResult.paper_id,
            external_ids: alertResult.external_ids,
            is_open_access: alertResult.is_open_access,
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const sourceId = source.id as string;

      // Add to folder if selected
      if (folderId && folderId !== 'none') {
        await supabase
          .from('folder_sources')
          .insert({ folder_id: folderId, source_id: sourceId });
      }

      // Mark alert result as imported
      await supabase
        .from('alert_results')
        .update({
          imported: true,
          imported_at: new Date().toISOString(),
          source_id: sourceId,
        })
        .eq('id', alertResult.id);

      // Trigger text extraction if there's a URL to download
      if (alertResult.open_access_pdf_url || alertResult.url) {
        extractText({ source_id: sourceId }).catch((err) => { console.error('[Import] text extraction failed:', err); });
      }

      setImported(true);
      setCreatedSourceId(sourceId);
      toast.success('Paper imported successfully!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import paper';
      toast.error(message);
    } finally {
      setImporting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h2 className="mt-4 text-lg font-semibold text-slate-900">Import Error</h2>
        <p className="mt-2 text-slate-600">{error}</p>
        <Button
          variant="outline"
          className="mt-6"
          onClick={() => navigate('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  if (!alertResult) return null;

  const authors = alertResult.authors?.join(', ') || 'Unknown authors';
  const s2Url = alertResult.url || `https://www.semanticscholar.org/paper/${alertResult.paper_id}`;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-slate-900">Import Paper</h1>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <CardTitle className="text-lg leading-snug">
              {alertResult.title || 'Untitled Paper'}
            </CardTitle>
            {alertResult.is_open_access && (
              <Badge variant="secondary" className="shrink-0 bg-green-100 text-green-700">
                Open Access
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Authors */}
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <span>{authors}</span>
          </div>

          {/* Year & Journal */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
            {alertResult.year && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>{alertResult.year}</span>
              </div>
            )}
            {alertResult.journal && (
              <div className="flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-slate-400" />
                <span>{alertResult.journal}</span>
              </div>
            )}
          </div>

          {/* DOI */}
          {alertResult.doi && (
            <div className="flex items-center gap-1.5 text-sm">
              <FileText className="h-4 w-4 text-slate-400" />
              <a
                href={`https://doi.org/${alertResult.doi}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
              >
                {alertResult.doi}
              </a>
            </div>
          )}

          {/* Abstract */}
          {alertResult.abstract && (
            <div className="rounded-lg bg-slate-50 p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Abstract
              </h3>
              <p className="text-sm leading-relaxed text-slate-700">
                {alertResult.abstract}
              </p>
            </div>
          )}

          {/* Semantic Scholar link */}
          <a
            href={s2Url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
          >
            View on Semantic Scholar
            <ExternalLink className="h-3.5 w-3.5" />
          </a>

          {/* Import section */}
          {imported ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Paper imported successfully!</span>
              </div>
              {createdSourceId && (
                <Link
                  to={`/chat/${createdSourceId}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:underline"
                >
                  Open in Chat
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4 border-t pt-4">
              {/* Folder selector */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Add to folder (optional)
                </label>
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="No folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No folder</SelectItem>
                    {folders?.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleImport}
                disabled={importing}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Import to MakeMyDoc
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
